import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { buildConfigurableTooltipFormatter } from '../../utils/chart.utils';

@Component({
  selector: 'app-pie-chart',
  templateUrl: './pie-chart.component.html',
  styleUrls: ['./pie-chart.component.scss'],
})
export class PieChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  protected myChart!: echarts.ECharts;
  protected allSliceNames: string[] = [];

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private reflowSub?: Subscription;

  ngAfterViewInit(): void {
    // Attach wrapper as 'legend-only' chart type (pie/donut)
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => this.allSliceNames,
      getCategories: () => [],
    }, 'legend-only');

    // Initial load (uses pre-existing filters if available, e.g. from auto-selection)
    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    this.configService.getConfig(this.data.apiUrl, this.data, filters)?.subscribe({
      next: (res: any) => {
        if (!res) return;
        const dataKey = this.data?.dataKey;
        const chartData = dataKey ? res?.[dataKey] : (res?.data || res);
        if (Array.isArray(chartData)) {
          this.wrapper.setSourceData(chartData, this.data?.categoryKey || 'name');
        }
        this.renderChart(chartData);
        this.addResizeListener();
      },
      error: () => {
        this.myChart.showLoading();
      },
    });
  }

  protected renderChart(chartData?: [{ name: string; value: number }]): void {
    this.allSliceNames = (chartData || []).map((item) => item.name);

    const option = {
      title: {
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        formatter: buildConfigurableTooltipFormatter(this.data?.chartConfig?.tooltip) || '{a} <br/>{b} : {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        left: 'left',
      },
      series: [
        {
          name: this.data.title || '',
          type: 'pie',
          radius: '50%',
          data: chartData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };

    this.myChart.setOption(option);
    setTimeout(() => {
      this.myChart.hideLoading();
    }, 1000);

    // Let wrapper re-apply visual state and wire click/legend events
    this.wrapper.afterRender();
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.resizeHandler);
  }

  protected resizeHandler = () => this.myChart?.resize();

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    window.removeEventListener('resize', this.resizeHandler);
    this.myChart?.dispose();
  }
}
